import React from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import * as Yup from 'yup';

import buildProfileLocationString from '@zr/utilities/buildProfileLocationString';
import LabeledField from '@zr/components/Fields/LabeledField';

import {
  US,
  CA,
  COUNTRY_CODE_MAP,
  COUNTRY_NAME,
  COUNTRY_DISPLAY,
  ZIPCODE_NAME,
  LOCATION_NAME,
  getZipcodeDisplay,
  isUSorCA,
} from '@zr/jobseeker-public/apps/jobseeker-collect-additional-information/utilities';

import ZipcodeAutocomplete from '@zr/jobseeker-public/apps/jobseeker-collect-additional-information/components/ZipcodeAutocomplete/ZipcodeAutocomplete';

import FormActions from '../../../components/FormActions';
import QuestionLabel from '../../../components/QuestionLabel';

import IndexedLegend from './IndexedLegend';
import FormButtons from './FormButtons';

import { patchProfileForm } from './utilities';

import './Location.less';

export function Location({
  index, ipCountryCode, locationAutocompleteUrl, onContinue, onError, onSkip, profile,
  submitButtonText, totalScreeningQuestions,
  isInJs6650Test, setIsLoading,
}) {
  const handleSave = async (values, formik) => {
    // `setIsLoading(true)` so that we can disable the close button while the
    // screen is processing/loading
    setIsLoading(true);
    const { location, country } = values;
    if (isUSorCA(country)) {
      // convert geoac location object into profile friendly fields
      const profileData = {
        location: buildProfileLocationString({
          country: location.cc,
          city: location.city,
          state: location.admin1_code,
        }),
        city: location.city,
        state: location.admin1_code,
        country: location.cc,
        // prioritize geoac's zipcode over inputted zipcode, though they should be the same
        zip_code: location.postal_code,
      };
      await patchProfileForm(profileData, formik, onContinue, onError);
    }
    // if Other country, literally do nothing since we don't save any profile data on that
    onContinue();
  };

  return (
    <Formik
      initialValues={{
        [COUNTRY_NAME]: profile.country || ipCountryCode || '',
        [ZIPCODE_NAME]: '',
      }}
      onSubmit={handleSave}
      // Yup.lazy is used to dynamically recalculate the schema and fresh zipcodeDisplay when values change
      // otherwise the schema and zipcodeDisplay grow stale when values are updated
      // https://github.com/formik/formik/issues/1228#issuecomment-640869857
      validationSchema={() => Yup.lazy((values) => (
        Yup.object({
          [COUNTRY_NAME]: Yup.string().required(`${COUNTRY_DISPLAY} is required`),
          // location required if country is US or CA
          [ZIPCODE_NAME]: isUSorCA(values[COUNTRY_NAME]) ? Yup.string().required(`${getZipcodeDisplay(values[COUNTRY_NAME])} is required`) : null,
          [LOCATION_NAME]: isUSorCA(values[COUNTRY_NAME]) ? Yup.object({
            // with {} as the default value, validate a selected location by checking for a name property
            name: Yup.string().required(`Please enter a valid ${getZipcodeDisplay(values[COUNTRY_NAME])}`),
          }) : null,
        })
      ))}
    >
      {({
        errors, isSubmitting, handleSubmit, setValues, touched, values,
      }) => {
        const countryValue = values[COUNTRY_NAME];
        const showZipcodeAutocomplete = countryValue === US || countryValue === CA;

        // use event handlers rather than watching values with effect hooks, cause the latter messes up initial values rendering
        const handleCountryChange = (e) => {
          setValues({
            ...values,
            [LOCATION_NAME]: {},
            [ZIPCODE_NAME]: '',
            [COUNTRY_NAME]: e.target.value,
          });
        };

        const handleZipcodeChange = (e) => {
          // reset their selected location if they've changed their input
          setValues({
            ...values,
            [LOCATION_NAME]: {},
            [ZIPCODE_NAME]: e.target.value,
          });
        };

        const handleLocationSelect = (item) => {
          setValues({
            ...values,
            [LOCATION_NAME]: item.selectedItem,
            [ZIPCODE_NAME]: item.inputValue,
          });
        };

        const legendText = isUSorCA(countryValue)
          ? `Please provide your country and ${getZipcodeDisplay(values[COUNTRY_NAME])}.`
          : 'Please provide your country of residence.';

        return (
          <form className="Suggestion Location" onSubmit={handleSubmit}>
            <fieldset>
              {isInJs6650Test && (
                <QuestionLabel
                  as="legend"
                  html={legendText}
                  number={index}
                  totalQuestions={totalScreeningQuestions}
                />
              )}
              {!isInJs6650Test && (
                <IndexedLegend index={index} text={legendText} />
              )}
              <LabeledField
                as="select"
                id={COUNTRY_NAME}
                input={{
                  disabled: isSubmitting,
                  name: COUNTRY_NAME,
                  onChange: handleCountryChange,
                  value: countryValue,
                }}
                label={COUNTRY_DISPLAY}
                meta={{
                  error: errors[COUNTRY_NAME],
                  touched: touched[COUNTRY_NAME],
                }}
                placeholder={COUNTRY_DISPLAY}
              >
                {Object.entries(COUNTRY_CODE_MAP).map(([countryKey, countryDisplay]) => (
                  <option key={countryKey} value={countryKey}>{countryDisplay}</option>
                ))}
              </LabeledField>
              {showZipcodeAutocomplete && (
                <ZipcodeAutocomplete
                  countryValue={countryValue}
                  disabled={isSubmitting}
                  inputError={errors[ZIPCODE_NAME]}
                  inputValue={values[ZIPCODE_NAME]}
                  locationAutocompleteUrl={locationAutocompleteUrl}
                  locationError={errors[LOCATION_NAME]?.name}
                  name={ZIPCODE_NAME}
                  onChange={handleZipcodeChange}
                  onSelectedItemChange={handleLocationSelect}
                  touched={touched[ZIPCODE_NAME]}
                />
              )}
              {isInJs6650Test && (
                <FormActions
                  disableContinue={isSubmitting}
                  disableSkip={isSubmitting}
                  onSkip={onSkip}
                  submitButtonText={submitButtonText}
                />
              )}
              {!isInJs6650Test && (
                <FormButtons disabled={isSubmitting} onSkip={onSkip} submitButtonText={submitButtonText} />
              )}
            </fieldset>
          </form>
        );
      }}
    </Formik>
  );
}

Location.propTypes = {
  index: PropTypes.number.isRequired,
  ipCountryCode: PropTypes.string,
  isInJs6650Test: PropTypes.bool,
  locationAutocompleteUrl: PropTypes.string.isRequired,
  onContinue: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onSkip: PropTypes.func,
  profile: PropTypes.shape({
    country: PropTypes.string,
  }),
  setIsLoading: PropTypes.func,
  submitButtonText: PropTypes.string,
  totalScreeningQuestions: PropTypes.number.isRequired,
};

Location.defaultProps = {
  ipCountryCode: null,
  isInJs6650Test: false,
  onSkip: null,
  profile: {},
  setIsLoading: () => {},
  submitButtonText: 'Continue',
};

export default Location;
